<script setup>
import { ref, watchEffect, watch, useTemplateRef, computed } from 'vue';
import useLegacyBridge from "@/composables/useLegacyBridge";
import ModelSelector from '@/components/ModelSelector.vue';

const URL_ORIGIN = window.location.origin;
const PATHNAME = window.location.pathname;

const props = defineProps({
  searchTerm: {
    type: String,
    required: true,
  },
  searchType: {
    type: String,
    default: 'search', // 'search' or 'coords'
  },
  lat: {
    type: [String, Number],
    default: null,
  },
  lon: {
    type: [String, Number],
    default: null,
  },
  zoom: {
    type: Number,
    default: 9,
  },
  modelId: {
    type: [String, Number],
    default: -1,
  },
  containerId: {
    type: [String, Number],
    default: 'live-wind-map',
  },
});

const emit = defineEmits(['mapMoved', 'modelChanged', 'spotClicked', 'mapLoaded']);

const mapRef = useTemplateRef('map-ref');

const { cosmos } = useLegacyBridge();

let cosmosUI = null;
const cosmosRef = ref(null);
const selectedModelId = ref(-1);
const coords = ref({ lat: props.lat, lon: props.lon });
const mapZoom = ref(null);
const bounds = ref(null);

const uiOptions = {
  useJwxUI: false,
  tempestMeta: true,
  viewId: '999',
  openLegend: true,
  miniGraphOptions: {
    showModelData: false,
    extents: [-4, 4],
  },
  uiElements: [
    jweather.UIElements.LEGEND,
    jweather.UIElements.QUICKVIEW,
  ],
  regionalProducts: {
    products: [],
    productSettings: {},
  },
  legend: {
    allowClose: false,
    obsLegend: {
      isHurrnet: false,
    },
  },
};

const mapOptions = {
  minZoom: 3,
  maxZoom: 20,
  searchType: "search",
  mapMoved: function (center, zoom) {
    const mapCenter = cosmosUI.jwMap.map.getCenter();

    const newLat = parseFloat(mapCenter.lat().toFixed(3));
    let newLon = mapCenter.lng();

    // Normalize longitude due to google maps sending back
    // out of range longitude values when crossing prime meridian
    if (newLon < -180) {
      newLon += 360;
    } else if (newLon > 180) {
      newLon -= 360
    }

    newLon = parseFloat(newLon.toFixed(3));

    coords.value = { lat: newLat, lon: newLon }
    mapZoom.value = zoom;

    updateURL({
      origin: URL_ORIGIN,
      pathname: PATHNAME,
      zoom,
      lat: newLat,
      lon: newLon,
    })

    updateBounds(cosmosUI.jwMap.map);
    
    emit('mapMoved', {
      center: `${newLat.toFixed(3)},${newLon.toFixed(3)}`,
      lat: newLat,
      lon: newLon,
      zoom,
    });
  },
};

function createView(modelId) {  
  return {
    name: "Flowmap with Observations",
    description: "Current observations with forecast map",
    mapMode: "fx",
    overlays: {
      fx: {
        modelId: modelId,
        param: jweather.paramWind,
        flowviz: true,
        showPast: true,
        transparency: 0.6,
        showLastFrameBeforeFuture: true,
        enableQuickView: false,
      },
      markers: {
        data: [jweather.paramWind],
        loc: [1, 100],
      },
    },
  };
}

function loadCosmos() {
  uiOptions.viewId = "999";
  uiOptions.regionalProducts.productSettings = {
    "999": createView(props.modelId || selectedModelId.value),
  };
  uiOptions.legend.targetSelector = `[data-map=legend-${props.containerId}]`;

  // Set map options based on search type
  if (props.searchType === 'search') {
    mapOptions.searchString = decodeURIComponent(props.searchTerm);
  } else if (props.searchType === 'coords') {
    mapOptions.lat = props.lat;
    mapOptions.lon = props.lon;
    mapOptions.zoom = props.zoom;
  }

  cosmosUI = cosmos(mapRef.value, uiOptions, mapOptions);
}

function initMapIdleEvent() {
  window.$EventBus.addEventListener('cosmos-map-idle', () => {
  
    addModelListChangedEvent();
  
    const mapCenter = cosmosUI.jwMap.map.getCenter();
    coords.value = { lat: mapCenter.lat(), lon: mapCenter.lng() }
    mapZoom.value = cosmosUI.jwMap.map.getZoom();
    bounds.value = updateBounds(cosmosUI.jwMap.map);
  
    emit('mapLoaded', { lat: coords.value.lat, lon: coords.value.lon, zoom: mapZoom.value });
  })
}

function addModelListChangedEvent() {
  google.maps.event.addListener(cosmosUI.jwMap, "model_list_changed", function (modelData) {
    if (modelData && modelData.model_summary) {
      bounds.value = updateBounds(cosmosUI.jwMap.map);
    }
  });
}

function updateURL({ origin, pathname, zoom = null, lat, lon}) {
  if (pathname.indexOf('/search/') > -1 || pathname.indexOf('/windlist-lifestyle/') > -1) {
    const baseSearchUrl = `${origin}/search/`;
    let newSearchUrl = `${baseSearchUrl}${lat.toFixed(3)}/${lon.toFixed(3)}`;

    if (zoom) {
      newSearchUrl += `/${zoom}`;
    }

    if (parseInt(selectedModelId.value) !== -1) {
      newSearchUrl += `/${selectedModelId.value}`;
    }

    if (import.meta.env.DEV) {
      console.log('Live Wind map url: ', newSearchUrl)
      return;
    }
    
    window.history.replaceState({}, '', newSearchUrl);
  }
}

function updateBounds(map) {
  const mapBounds = map.getBounds();
  const latMin = mapBounds.getSouthWest().lat();
  const lonMin = mapBounds.getSouthWest().lng();
  const latMax = mapBounds.getNorthEast().lat();
  const lonMax = mapBounds.getNorthEast().lng();
  
  return { latMin, lonMin, latMax, lonMax }
}

watchEffect(() => {
  if (mapRef.value && !cosmosUI) {
    initMapIdleEvent();
    loadCosmos();
  }
});

function handleModelChange(model) {
  cosmosUI.showView('999', createView(model));

  updateURL({
    origin: URL_ORIGIN,
    pathname: PATHNAME,
    zoom: mapZoom.value,
    lat: coords.value.lat,
    lon: coords.value.lon,
    modelId: model,
  })
}
</script>

<template>
  <div class="jwx-data-container__controls">
    <div class="jwx-control-bar">
      <ModelSelector
        v-if="bounds || coords.lat && coords.lon"
        id="live-wind-model-selector"
        :bounds="bounds"
        v-model="selectedModelId"
        @input:change="handleModelChange"
      />
    </div>
  </div>
  <div class="jwx-data-container__content">
    <div
    ref="map-ref"
    :id="`live-wind-map-${props.containerId}`"
    class="jwx-live-wind-map"
    style="height: 400px; width: 100%; overflow: hidden; position: relative;"
    ></div>
    <div class="jwx-live-wind-map__legend" :data-map="`legend-${props.containerId}`"></div>
  </div>  
</template>
